/**
 * DealNexa Permissions System
 *
 * Covers both DD platform permissions and the PI (Portfolio Intelligence) module.
 * DD permissions use simple string keys (e.g. 'view_deals').
 * PI permissions use the 'pi.*' namespace (e.g. 'pi.vcp.edit').
 */

// ── DD Role Defaults ──────────────────────────────────────────────────────────
const DD_ROLE_DEFAULTS = {
  admin:  { view_documents: true,  view_deals: true,  edit: true,  save: true,  download: true,  import: true,  add_members: true,  audit_trail: true  },
  member: { view_documents: true,  view_deals: true,  edit: true,  save: true,  download: true,  import: true,  add_members: false, audit_trail: false },
  viewer: { view_documents: true,  view_deals: true,  edit: false, save: false, download: false, import: false, add_members: false, audit_trail: false },
  custom: { view_documents: false, view_deals: false,  edit: false, save: false, download: false, import: false, add_members: false, audit_trail: false },
};

// ── PI Permission Matrix per role ────────────────────────────────────────────
// true = allowed, false / undefined = denied
const PI_ROLE_PERMISSIONS = {
  admin: {
    // Admins have everything
    'pi.dashboard.view':       true,
    'pi.portco.view_all':      true,
    'pi.vcp.view':             true,
    'pi.vcp.edit':             true,
    'pi.bridge.view':          true,
    'pi.bridge.edit':          true,
    'pi.returns.view':         true,
    'pi.returns.edit':         true,
    'pi.synergies.view':       true,
    'pi.synergies.edit':       true,
    'pi.addons.view':          true,
    'pi.addons.edit':          true,
    'pi.exit.view':            true,
    'pi.exit.edit':            true,
    'pi.talent.view':          true,
    'pi.talent.edit':          true,
    'pi.cash.view':            true,
    'pi.cash.edit':            true,
    'pi.lp_reports.view':      true,
    'pi.lp_reports.create':    true,
    'pi.lp_reports.approve':   true,
    'pi.lp_reports.send':      true,
    'pi.settings.manage':      true,
    'pi.integrations.manage':  true,
    'pi.import':               true,
  },

  gp: {
    'pi.dashboard.view':       true,
    'pi.portco.view_all':      true,
    'pi.vcp.view':             true,
    'pi.vcp.edit':             true,
    'pi.bridge.view':          true,
    'pi.bridge.edit':          true,
    'pi.returns.view':         true,
    'pi.returns.edit':         true,
    'pi.synergies.view':       true,
    'pi.synergies.edit':       true,
    'pi.addons.view':          true,
    'pi.addons.edit':          true,
    'pi.exit.view':            true,
    'pi.exit.edit':            true,
    'pi.talent.view':          true,
    'pi.talent.edit':          true,
    'pi.cash.view':            true,
    'pi.cash.edit':            true,
    'pi.lp_reports.view':      true,
    'pi.lp_reports.create':    true,
    'pi.lp_reports.approve':   true,
    'pi.lp_reports.send':      true,
    'pi.settings.manage':      true,
    'pi.integrations.manage':  true,
    'pi.import':               true,
  },

  operating_partner: {
    'pi.dashboard.view':       true,
    'pi.portco.view_all':      true,
    'pi.vcp.view':             true,
    'pi.vcp.edit':             true,
    'pi.bridge.view':          true,
    'pi.bridge.edit':          true,
    'pi.returns.view':         true,
    'pi.returns.edit':         false,
    'pi.synergies.view':       true,
    'pi.synergies.edit':       true,
    'pi.addons.view':          true,
    'pi.addons.edit':          true,
    'pi.exit.view':            true,
    'pi.exit.edit':            true,
    'pi.talent.view':          true,
    'pi.talent.edit':          true,
    'pi.cash.view':            true,
    'pi.cash.edit':            false,
    'pi.lp_reports.view':      true,
    'pi.lp_reports.create':    true,
    'pi.lp_reports.approve':   false,
    'pi.lp_reports.send':      false,
    'pi.settings.manage':      false,
    'pi.integrations.manage':  false,
    'pi.import':               false,
  },

  // VP — existing DD role extended with PI access (assigned portcos only)
  vp: {
    'pi.dashboard.view':       true,
    'pi.portco.view_assigned': true,
    'pi.vcp.view':             true,
    'pi.vcp.edit':             true,
    'pi.bridge.view':          true,
    'pi.bridge.edit':          false,
    'pi.returns.view':         true,
    'pi.returns.edit':         false,
    'pi.synergies.view':       true,
    'pi.synergies.edit':       false,
    'pi.addons.view':          true,
    'pi.addons.edit':          true,
    'pi.exit.view':            true,
    'pi.exit.edit':            false,
    'pi.talent.view':          true,
    'pi.talent.edit':          false,
    'pi.cash.view':            false,
    'pi.cash.edit':            false,
    'pi.lp_reports.view':      false,
    'pi.lp_reports.create':    false,
    'pi.import':               false,
  },

  // Analyst — existing DD role, limited PI read
  analyst: {
    'pi.dashboard.view':       true,
    'pi.portco.view_assigned': true,
    'pi.vcp.view':             true,
    'pi.vcp.edit':             false,
    'pi.bridge.view':          false,
    'pi.returns.view':         false,
    'pi.addons.view':          true,
    'pi.addons.edit':          false,
    'pi.lp_reports.view':      false,
    'pi.import':               false,
  },

  // Advisor — existing DD role, read-only on assigned portcos
  advisor: {
    'pi.dashboard.view':       true,
    'pi.portco.view_assigned': true,
    'pi.vcp.view':             true,
    'pi.bridge.view':          true,
    'pi.returns.view':         true,
    'pi.lp_reports.view':      false,
    'pi.import':               false,
  },

  // LP — new role, fund-level summary + sent reports only
  lp: {
    'pi.dashboard.view':       true,  // fund-level only
    'pi.lp_reports.view':      true,  // only 'sent' reports
    'pi.portco.view_summary':  true,  // high-level KPIs only
    'pi.import':               false,
  },

  // Portco Management — portco-side users, their portco only
  portco_management: {
    'pi.vcp.view':             true,
    'pi.vcp.edit':             true,
    'pi.bridge.view':          true,
    'pi.talent.view':          true,
    'pi.talent.edit':          true,
    'pi.lp_reports.view':      false,
    'pi.import':               false,
  },
};

// ── DD permission check ───────────────────────────────────────────────────────
export function can(user, perm) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.permissions) return !!user.permissions[perm];
  const defaults = DD_ROLE_DEFAULTS[user.role] || DD_ROLE_DEFAULTS['member'];
  return !!defaults[perm];
}

// ── PI permission check ───────────────────────────────────────────────────────
/**
 * Check if a user has a specific PI permission.
 * Returns false if pi_enabled is false on the user's org.
 *
 * @param {object} user  - user object with .role and optional .org.pi_enabled
 * @param {string} perm  - PI permission string, e.g. 'pi.vcp.edit'
 */
export function hasPI(user, perm) {
  if (!user) return false;

  const role = user.role;

  // Admin always has everything, regardless of pi_enabled flag
  if (role === 'admin') return true;

  // Check org-level PI feature flag for non-admins
  const piEnabled = user.org?.pi_enabled ?? user.pi_enabled ?? false;
  if (!piEnabled) return false;

  // Check role-specific PI permissions
  const rolePerms = PI_ROLE_PERMISSIONS[role];
  if (!rolePerms) return false;

  return !!rolePerms[perm];
}

/**
 * Returns true if the user has any PI access at all (to show PI nav button).
 * Even LP users should see the button (they have pi.dashboard.view).
 */
export function hasPIAccess(user) {
  if (!user) return false;
  const role = user.role;
  if (role === 'admin') return true;
  const piEnabled = user.org?.pi_enabled ?? user.pi_enabled ?? false;
  if (!piEnabled) return false;
  return !!(PI_ROLE_PERMISSIONS[role]);
}

/**
 * Returns the set of tab slugs visible to the user for portco views.
 */
export function getPIVisibleTabs(user) {
  if (!user) return [];
  const role = user.role;

  const ALL_TABS = ['overview','vcp','bridge','synergies','addons','returns','exit','talent','cash','lp','dd'];

  if (role === 'admin' || role === 'gp' || role === 'operating_partner') return ALL_TABS;

  if (role === 'vp') return ['overview','vcp','bridge','synergies','addons','returns','exit','talent','cash','dd'];

  if (role === 'analyst') return ['overview','vcp','addons','dd'];

  if (role === 'advisor') return ['overview','vcp','bridge','returns','dd'];

  if (role === 'portco_management') return ['overview','vcp','talent'];

  if (role === 'lp') return []; // LP uses /pi/lp-portal, not portco tabs

  return ['overview'];
}
